import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, UserCheck, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import CloseButton from '@components/common/CloseButton';
import ModalLoadingOverlay from '@components/common/ModalLoadingOverlay';
import Message from '@components/common/Message';
import WarningNotice from '@components/common/WarningNotice';
import { Assessment, ApiError } from '@app-types';
import { schedulerService, userService } from '@services';
import { statusToVariant } from '@utils/badgeMappers';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import { formatPersonName } from '@utils/stringUtils';
import { LoadingState } from '@components/common/loading';
import styles from './AssessmentAssignment.module.css';

interface AssessmentAssignmentProps {
  assessment: Assessment;
  onClose: () => void;
  onAssigned: () => void;
}

const AssessmentAssignment: React.FC<AssessmentAssignmentProps> = ({ assessment, onClose, onAssigned }) => {
  const [selectedAssessorId, setSelectedAssessorId] = useState<number | null>(assessment.assessor_id || null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch available assessors
  const { data: assessors, isLoading: loadingAssessors } = useQuery({
    queryKey: ['users', 'Assessor'],
    queryFn: () => userService.getUsers('Assessor')
  });

  // Assignment mutation
  // Assessments have no assessor_id of their own — the assessor lives on the
  // underlying meeting, so reassignment must go through the scheduler flow.
  const assignMutation = useMutation({
    mutationFn: (assessorId: number) =>
      schedulerService.updateScheduleItemAssessor(null, assessorId, assessment.contract_id),
    onSuccess: async () => {
      // Invalidate related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', assessment.assessment_id] }),
        queryClient.invalidateQueries({ queryKey: ['assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-table'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['supplier-assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['contract-assessments'] })
      ]);

      // Explicitly refetch key queries
      queryClient.refetchQueries({ queryKey: ['assessments-counts'] });
      queryClient.refetchQueries({ queryKey: ['assessments-table'] });

      onAssigned();
      onClose();
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || apiError.message || 'Failed to assign assessor';
      setError(errorMessage);
    }
  });

  // Unassignment mutation
  const unassignMutation = useMutation({
    mutationFn: () => schedulerService.updateScheduleItemAssessor(null, null, assessment.contract_id),
    onSuccess: async () => {
      // Invalidate related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', assessment.assessment_id] }),
        queryClient.invalidateQueries({ queryKey: ['assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-table'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['supplier-assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['contract-assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] })
      ]);

      // Explicitly refetch key queries
      queryClient.refetchQueries({ queryKey: ['assessments-counts'] });
      queryClient.refetchQueries({ queryKey: ['assessments-table'] });

      onAssigned();
      onClose();
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || apiError.message || 'Failed to unassign assessor';
      setError(errorMessage);
    }
  });

  const handleAssign = () => {
    if (!selectedAssessorId) {
      setError('Please select an assessor');
      return;
    }

    setError(null);
    assignMutation.mutate(selectedAssessorId);
  };

  const handleUnassign = () => {
    setError(null);
    unassignMutation.mutate();
  };

  const isAssigned = (assessorId: number) => {
    return assessment.assessor_id === assessorId;
  };

  const isLoading = assignMutation.isPending || unassignMutation.isPending;
  const currentAssessor = assessors?.find((a) => a.id === assessment.assessor_id);

  return (
    <div className={styles.assignmentModalOverlay} data-test-id="assignment-modal-overlay">
      <div className={styles.assignmentModal} data-test-id="assignment-modal">
        {/* Loading Overlay */}
        {isLoading && <ModalLoadingOverlay message="Processing request..." />}

        <div className={styles.assignmentHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <User size={24} color="#3b82f6" />
            </div>
            <div className={styles.headerText}>
              <h3 data-test-id="assignment-modal-title">Assign Assessor</h3>
              <p>
                You are about to assign or reassign the assessor. This will determine who conducts this assessment and
                evaluates the supplier's compliance.
              </p>
            </div>
          </div>
          <CloseButton onClick={onClose} disabled={isLoading} />
        </div>

        <div className={styles.assignmentContent}>
          {/* Assessment Information */}
          <div className={styles.contractInfo}>
            <h4>{assessment.assessment_name}</h4>
            <p>
              {assessment.supplier_name} • {assessment.tier_name}
            </p>
            <div className={styles.contractMeta}>
              <Badge variant={statusToVariant(assessment.assessment_status)}>{getAssessmentStatusLabel(assessment.assessment_status)}</Badge>
              <span className={styles.currentManager}>
                Current: {currentAssessor ? formatPersonName(currentAssessor) : 'Unassigned'}
              </span>
            </div>
          </div>

          {/* Assessor Selection */}
          <div className={styles.managerSelection}>
            <h5>Select Assessor</h5>

            {loadingAssessors && <LoadingState message="Loading available assessors..." />}
            {!loadingAssessors && assessors && assessors.length > 0 && (
              <div className={styles.managersGrid} data-test-id="assessors-grid">
                {assessors.map((assessor) => (
                  <button
                    type="button"
                    key={assessor.id}
                    className={clsx(styles.managerOption, {
                      [styles.selected]: selectedAssessorId === assessor.id
                    })}
                    onClick={() => !isLoading && setSelectedAssessorId(assessor.id)}
                  >
                    <div className={styles.optionHeader}>
                      <User size={20} color={selectedAssessorId === assessor.id ? '#3b82f6' : '#6b7280'} />
                      <span className={styles.optionLabel}>
                        {formatPersonName(assessor)}
                      </span>
                      {isAssigned(assessor.id) && (
                        <div className={styles.currentBadge}>
                          <UserCheck size={16} />
                          Current
                        </div>
                      )}
                    </div>
                    <p className={styles.optionDescription}>{assessor.email}</p>
                  </button>
                ))}
              </div>
            )}
            {!loadingAssessors && (!assessors || assessors.length === 0) && (
              <div className="empty-state">
                <User size={48} className="icon-muted" />
                <p>No assessors available</p>
              </div>
            )}
          </div>

          {/* Warning Notice */}
          {selectedAssessorId && selectedAssessorId !== assessment.assessor_id && (
            <WarningNotice icon={<AlertCircle size={16} />}>
              <span>
                Assigning a new assessor will change who is responsible for conducting this assessment and evaluating
                compliance.
              </span>
            </WarningNotice>
          )}

          {/* Status Messages */}
          {error && (
            <Message variant="error" icon={<AlertCircle size={16} />}>
              {error}
            </Message>
          )}
        </div>

        <div className={styles.assignmentFooter}>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>

          {currentAssessor && (
            <Button variant="secondary" onClick={handleUnassign} disabled={isLoading}>
              {unassignMutation.isPending ? 'Unassigning...' : 'Unassign Assessor'}
            </Button>
          )}

          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedAssessorId || isLoading || selectedAssessorId === assessment.assessor_id}
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign Assessor'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAssignment;
